import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  Clock3,
  Headphones,
  Languages,
  Lock,
  Mic2,
  Pause,
  PencilLine,
  Play,
  Square,
  Volume2,
} from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantCoachPanel from "../components/applicant/ApplicantCoachPanel";
import { useApplicantCoach } from "../components/applicant/ApplicantCoachContext";
import KvpReportTrigger from "../components/kvp/KvpReportTrigger";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import B1SpeakingAudioRecorder from "../components/language/B1SpeakingAudioRecorder";
import config from "../config/config";
import { buildAuthFetchOptions, clearStoredAuth } from "../utils/authSession";

const WRITTEN_SECTION_ORDER = ["listening", "reading", "language_elements", "writing"];
const SECTION_ICONS = {
  listening: Headphones,
  reading: BadgeCheck,
  language_elements: Languages,
  writing: PencilLine,
};

async function fetchJson(path, options = {}) {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const response = await fetch(
    `${config.API_BASE_URL}${path}`,
    buildAuthFetchOptions({
      ...options,
      headers: {
        ...(!isFormData && options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
    })
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

async function fetchBlob(path, options = {}) {
  const response = await fetch(
    `${config.API_BASE_URL}${path}`,
    buildAuthFetchOptions({
      ...options,
      headers: {
        ...(options.headers || {}),
      },
    })
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return response.blob();
}

function cloneValue(value, fallback) {
  try {
    return JSON.parse(JSON.stringify(value ?? fallback));
  } catch (_err) {
    return fallback;
  }
}

function formatDate(value) {
  if (!value) return "Noch offen";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatAttemptType(value) {
  if (value === "baseline") return "Erster Test";
  if (value === "follow_up") return "Neuer Test";
  return "Noch offen";
}

function formatReviewStatus(value) {
  if (value === "reviewed") return "Geprueft";
  if (value === "pending") return "In Pruefung";
  if (value === "not_required") return "Keine manuelle Pruefung noetig";
  return value || "Noch offen";
}

function formatScore(value) {
  return value === null || value === undefined ? "Noch offen" : `${Math.round(Number(value))}`;
}

function extractModules(attempt) {
  return attempt?.answers?.modules || {};
}

function addMinutes(isoString, minutes) {
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setMinutes(parsed.getMinutes() + Number(minutes || 0));
  return parsed.toISOString();
}

function addSeconds(isoString, seconds) {
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setSeconds(parsed.getSeconds() + Number(seconds || 0));
  return parsed.toISOString();
}

function getSectionMeta(modules, sectionKey) {
  return modules?.[sectionKey] || null;
}

function buildDefaultSectionProgress(modules) {
  const now = new Date().toISOString();
  const progress = {};
  WRITTEN_SECTION_ORDER.forEach((sectionKey, index) => {
    const meta = getSectionMeta(modules, sectionKey);
    progress[sectionKey] = {
      status: index === 0 ? "active" : "locked",
      started_at: index === 0 ? now : null,
      deadline_at: index === 0 ? addMinutes(now, meta?.duration_minutes || 0) : null,
      completed_at: null,
      unlocked_at: index === 0 ? now : null,
    };
  });
  return progress;
}

function buildDefaultListeningRuntime(modules, sectionProgress) {
  const listening = modules?.listening || {};
  const startedAt = sectionProgress?.listening?.started_at || new Date().toISOString();
  const parts = {};
  (listening.parts || []).forEach((part, index) => {
    parts[part.key] = {
      status: index === 0 ? "reading_time" : "locked",
      phase: index === 0 ? "reading_time" : "locked",
      play_count: 0,
      allowed_plays: Number(part.allowed_plays || 1),
      opened_at: index === 0 ? startedAt : null,
      phase_started_at: index === 0 ? startedAt : null,
      phase_deadline_at: index === 0 ? addSeconds(startedAt, part.reading_time_seconds || 0) : null,
      completed_at: null,
      last_error: null,
    };
  });
  return {
    current_part_key: listening.parts?.[0]?.key || null,
    audio_ready: true,
    parts,
  };
}

function normalizeSectionProgress(saved, modules) {
  const fallback = buildDefaultSectionProgress(modules);
  const progress = {};
  WRITTEN_SECTION_ORDER.forEach((sectionKey) => {
    progress[sectionKey] = {
      ...fallback[sectionKey],
      ...(saved?.[sectionKey] || {}),
    };
  });
  return progress;
}

function normalizeListeningRuntime(saved, modules, sectionProgress) {
  const fallback = buildDefaultListeningRuntime(modules, sectionProgress);
  const next = {
    current_part_key: saved?.current_part_key || fallback.current_part_key,
    audio_ready: true,
    parts: {},
  };
  (modules?.listening?.parts || []).forEach((part) => {
    next.parts[part.key] = {
      ...fallback.parts[part.key],
      ...(saved?.parts?.[part.key] || {}),
      allowed_plays: Number(part.allowed_plays || 1),
    };
  });
  return next;
}

function findActiveSectionKey(sectionProgress) {
  return WRITTEN_SECTION_ORDER.find((sectionKey) => sectionProgress?.[sectionKey]?.status === "active") || null;
}

function buildDraftState(attempt) {
  const saved = attempt?.answers?.draft_responses || {};
  const modules = extractModules(attempt);
  const sectionProgress = normalizeSectionProgress(saved.section_progress, modules);
  return {
    reading: saved.reading || {},
    listening: saved.listening || {},
    language_elements: saved.language_elements || {},
    writing_answer: saved.writing_answer || "",
    speaking_audio_meta: saved.speaking_audio_meta || null,
    speaking_status: attempt?.speaking_status || saved.speaking_status || "not_started",
    speaking_invalid_reason: attempt?.speaking_invalid_reason || saved.speaking_invalid_reason || "",
    section_progress: sectionProgress,
    active_section_key: saved.active_section_key || findActiveSectionKey(sectionProgress) || "listening",
    listening_runtime: normalizeListeningRuntime(saved.listening_runtime, modules, sectionProgress),
    modules,
  };
}

function buildAttemptPayload(draftState) {
  return {
    reading_answers: Object.entries(draftState.reading || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    listening_answers: Object.entries(draftState.listening || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    language_elements_answers: Object.entries(draftState.language_elements || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    writing_answer: draftState.writing_answer || "",
    section_progress: draftState.section_progress || {},
    active_section_key: draftState.active_section_key || null,
    listening_runtime: draftState.listening_runtime || null,
  };
}

function getSecondsRemaining(deadline) {
  if (!deadline) return null;
  const remaining = Math.floor((new Date(deadline).getTime() - Date.now()) / 1000);
  return Number.isFinite(remaining) ? Math.max(0, remaining) : null;
}

function formatCountdown(secondsLeft) {
  const safe = Math.max(0, Math.floor(Number(secondsLeft || 0)));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDurationLabel(minutes) {
  return `${Number(minutes || 0)} Minuten`;
}

function formatAudioTime(secondsValue) {
  if (secondsValue === null || secondsValue === undefined || Number.isNaN(Number(secondsValue))) {
    return "--:--";
  }
  const totalSeconds = Math.max(0, Math.floor(Number(secondsValue)));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatItemCountLabel(count) {
  const total = Number(count || 0);
  return `${total} ${total === 1 ? "Aufgabe" : "Aufgaben"}`;
}

function toCoachKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "b1_step";
}

function getB1CoachLabelEn(label) {
  switch (String(label || "").trim().toLowerCase()) {
    case "deutschsignal freigeben":
      return "release German signal";
    case "neuen versuch starten":
      return "start a new attempt";
    case "ersten versuch starten":
      return "start first attempt";
    case "hoerverstehen":
      return "listening";
    case "leseverstehen":
      return "reading";
    case "grammatik":
      return "grammar";
    case "schreibkompetenz":
      return "writing";
    case "muendliche pruefung":
      return "speaking section";
    case "freigabe fuer arbeitgeber":
      return "release for employers";
    case "b1-versuch starten":
      return "start B1 attempt";
    default:
      return "B1 step";
  }
}

function getStatusIcon(status) {
  if (status === "completed") return CheckCircle2;
  if (status === "active") return CircleDashed;
  return Lock;
}

function renderStatusLabel(status) {
  if (status === "completed") return "abgeschlossen";
  if (status === "active") return "aktiv";
  return "gesperrt";
}

function getCompletedSectionKeys(sectionProgress) {
  return new Set(WRITTEN_SECTION_ORDER.filter((sectionKey) => sectionProgress?.[sectionKey]?.status === "completed"));
}

function activateNextSections(sectionProgress, modules, completedKeys) {
  const now = new Date().toISOString();
  const nextProgress = {};
  let activeAssigned = false;
  WRITTEN_SECTION_ORDER.forEach((sectionKey) => {
    const previous = sectionProgress?.[sectionKey] || {};
    const meta = modules?.[sectionKey] || {};
    if (completedKeys.has(sectionKey)) {
      nextProgress[sectionKey] = {
        status: "completed",
        started_at: previous.started_at || previous.unlocked_at || now,
        deadline_at: previous.deadline_at || addMinutes(previous.started_at || now, meta.duration_minutes || 0),
        completed_at: previous.completed_at || now,
        unlocked_at: previous.unlocked_at || previous.started_at || now,
      };
      return;
    }
    if (!activeAssigned) {
      const startedAt = previous.started_at || now;
      nextProgress[sectionKey] = {
        status: "active",
        started_at: startedAt,
        deadline_at: previous.deadline_at || addMinutes(startedAt, meta.duration_minutes || 0),
        completed_at: null,
        unlocked_at: previous.unlocked_at || startedAt,
      };
      activeAssigned = true;
      return;
    }
    nextProgress[sectionKey] = {
      status: "locked",
      started_at: null,
      deadline_at: null,
      completed_at: null,
      unlocked_at: null,
    };
  });
  return nextProgress;
}

function withCompletedSection(state, sectionKey) {
  const next = cloneValue(state, state);
  const completedKeys = getCompletedSectionKeys(next.section_progress);
  completedKeys.add(sectionKey);
  next.section_progress = activateNextSections(next.section_progress, next.modules, completedKeys);
  next.active_section_key = findActiveSectionKey(next.section_progress);
  return next;
}

function withListeningPatch(state, partKey, patch) {
  const next = cloneValue(state, state);
  next.listening_runtime.parts[partKey] = {
    ...(next.listening_runtime.parts?.[partKey] || {}),
    ...patch,
  };
  return next;
}

function withCompletedListeningPart(state, partKey) {
  const next = cloneValue(state, state);
  const parts = next.modules?.listening?.parts || [];
  const index = parts.findIndex((entry) => entry.key === partKey);
  const now = new Date().toISOString();
  next.listening_runtime.parts[partKey] = {
    ...(next.listening_runtime.parts?.[partKey] || {}),
    status: "completed",
    phase: "completed",
    completed_at: next.listening_runtime.parts?.[partKey]?.completed_at || now,
    phase_deadline_at: null,
    last_error: null,
  };
  const nextPart = parts[index + 1];
  if (nextPart) {
    next.listening_runtime.current_part_key = nextPart.key;
    next.listening_runtime.parts[nextPart.key] = {
      ...(next.listening_runtime.parts?.[nextPart.key] || {}),
      status: "reading_time",
      phase: "reading_time",
      opened_at: next.listening_runtime.parts?.[nextPart.key]?.opened_at || now,
      phase_started_at: now,
      phase_deadline_at: addSeconds(now, nextPart.reading_time_seconds || 0),
      last_error: null,
    };
  } else {
    next.listening_runtime.current_part_key = null;
  }
  return next;
}

function recoverInterruptedListeningPlayback(state) {
  const next = cloneValue(state, state);
  const parts = Array.isArray(next.modules?.listening?.parts) ? next.modules.listening.parts : [];
  let changed = false;

  parts.forEach((part) => {
    const currentPartState = next.listening_runtime?.parts?.[part.key];
    const phase = String(currentPartState?.phase || currentPartState?.status || "").trim();
    if (phase !== "playing") return;

    changed = true;
    next.listening_runtime.current_part_key = part.key;
    next.listening_runtime.parts[part.key] = {
      ...(currentPartState || {}),
      status: "ready_to_play",
      phase: "ready_to_play",
      play_count: Math.max(0, Number(currentPartState?.play_count || 0) - 1),
      phase_started_at: new Date().toISOString(),
      phase_deadline_at: null,
      last_error: "Die vorherige Hoer-Wiedergabe wurde unterbrochen. Bitte starten Sie das Audio erneut.",
    };
  });

  return changed ? next : null;
}

function allListeningPartsComplete(state) {
  return (state.modules?.listening?.parts || []).every((part) => state.listening_runtime?.parts?.[part.key]?.status === "completed");
}

function getListeningPhaseLabel(partState, transportStatus) {
  if (transportStatus === "loading") return "Audio wird geladen";
  if (transportStatus === "paused") return "Audio pausiert";
  if (partState?.phase === "playing") return "Audio laeuft";
  if (partState?.phase === "post_window") return "Nachantwortfenster";
  if (partState?.phase === "ready_to_play") return "Bereit";
  if (partState?.phase === "reading_time") return "Einlesephase";
  if (partState?.phase === "awaiting_completion") return "Bereit zum Abschluss";
  if (partState?.status === "completed") return "Abgeschlossen";
  return "Gesperrt";
}

function sectionCanBeCompleted(state, sectionKey) {
  if (sectionKey !== "listening") return true;
  return allListeningPartsComplete(state);
}

function getSectionCards(modules, draftState) {
  return WRITTEN_SECTION_ORDER.map((sectionKey) => {
    const meta = modules?.[sectionKey] || {};
    return {
      key: sectionKey,
      title: meta.title || sectionKey,
      duration_minutes: Number(meta.duration_minutes || 0),
      total_items: Number(meta.total_items || 0),
      parts: Array.isArray(meta.parts) ? meta.parts : [],
      status: draftState.section_progress?.[sectionKey]?.status || "locked",
      deadline_at: draftState.section_progress?.[sectionKey]?.deadline_at || null,
      completed_at: draftState.section_progress?.[sectionKey]?.completed_at || null,
      items: Array.isArray(meta.items) ? meta.items : [],
      instructions: meta.instructions || "",
      prompt: meta.prompt || "",
      label: meta.label || "",
    };
  });
}

export default function ApplicantB1Workspace() {
  const navigate = useNavigate();
  const { registerCoachSurface } = useApplicantCoach();
  const [attempts, setAttempts] = useState([]);
  const [latestAttempt, setLatestAttempt] = useState(null);
  const [activeAttempt, setActiveAttempt] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
  const [clockTick, setClockTick] = useState(Date.now());
  const [listeningBusyPartKey, setListeningBusyPartKey] = useState("");
  const [listeningTransport, setListeningTransport] = useState({ partKey: "", status: "idle", currentTime: 0, duration: 0 });
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  const [selectedPartKey, setSelectedPartKey] = useState("");
  const [selectedSurfaceKey, setSelectedSurfaceKey] = useState("");
  const [activeQuestionKey, setActiveQuestionKey] = useState("");
  const skipDefaultQuestionRef = useRef(false);
  const audioRef = useRef(null);
  const audioUrlRef = useRef("");
  const focusStageRef = useRef(null);
  const oralStageRef = useRef(null);

  useEffect(() => {
    document.title = "Experts4Europe | Sprachtest B1";
  }, []);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    const timer = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [dialogOpen]);

  const releaseListeningAudioElement = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = "";
    }
  };

  const stopListeningAudio = () => {
    releaseListeningAudioElement();
    setListeningBusyPartKey("");
    setListeningTransport({ partKey: "", status: "idle", currentTime: 0, duration: 0 });
  };

  useEffect(() => () => stopListeningAudio(), []);

  const handleAuthError = (err) => {
    if (Number(err?.status) !== 401) return false;
    clearStoredAuth();
    setDialogOpen(false);
    setActionBusy(false);
    stopListeningAudio();
    setSessionExpiredMessage("Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an.");
    setSessionExpiredOpen(true);
    return true;
  };

  const loadLanguageTestState = async () => {
    setLoading(true);
    setError("");
    try {
      const [attemptsData, latestData] = await Promise.all([
        fetchJson("/api/language-tests/b1/me/attempts"),
        fetchJson("/api/language-tests/b1/me/latest"),
      ]);
      const rows = Array.isArray(attemptsData?.rows) ? attemptsData.rows : [];
      const draftAttempt = latestData?.draft_attempt || rows.find((row) => row.status === "draft") || null;
      setAttempts(rows);
      setLatestAttempt(latestData?.attempt || rows.find((row) => row.status !== "draft") || null);
      setActiveAttempt(draftAttempt);
      setDraftState(draftAttempt ? buildDraftState(draftAttempt) : null);
      setDraftDirty(false);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Sprachtest konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLanguageTestState();
  }, []);

  async function startAttempt(testType) {
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await fetchJson("/api/language-tests/b1/attempts", { method: "POST", body: JSON.stringify({ test_type: testType }) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setDialogOpen(true);
      setNotice(testType === "baseline" ? "Experts4Europe-B1-Prüfung gestartet." : "Neuer Experts4Europe-B1-Versuch gestartet.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Test konnte nicht gestartet werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveDraft(options = {}) {
    if (!activeAttempt || !draftState) return;
    setActionBusy(Boolean(!options.silent));
    setError("");
    if (!options.silent) setNotice("");
    try {
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/draft`, {
        method: "PATCH",
        body: JSON.stringify(buildAttemptPayload(draftState)),
      });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      if (!options.silent) setNotice("Entwurf gespeichert.");
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Entwurf konnte nicht gespeichert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  useEffect(() => {
    if (!dialogOpen || !activeAttempt || !draftDirty || actionBusy || listeningBusyPartKey) return undefined;
    const timer = window.setTimeout(() => {
      saveDraft({ silent: true });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [dialogOpen, activeAttempt, draftDirty, actionBusy, draftState, listeningBusyPartKey]);

  useEffect(() => {
    if (!dialogOpen || !draftState || listeningBusyPartKey || audioRef.current) return;
    const recoveredState = recoverInterruptedListeningPlayback(draftState);
    if (!recoveredState) return;
    setDraftState(recoveredState);
    setDraftDirty(true);
    setNotice("Die letzte Hoer-Wiedergabe wurde zurueckgesetzt. Bitte starten Sie das Audio erneut.");
  }, [dialogOpen, draftState, listeningBusyPartKey]);

  async function submitAttempt() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/submit`, {
        method: "POST",
        body: JSON.stringify(buildAttemptPayload(draftState)),
      });
      setDialogOpen(false);
      stopListeningAudio();
      setActiveAttempt(null);
      setDraftState(null);
      setDraftDirty(false);
      setNotice("Die Experts4Europe-B1-Prüfung wurde eingereicht.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Test konnte nicht eingereicht werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function uploadSpeakingAudio(file, durationMs) {
    if (!activeAttempt?.id) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("duration_ms", String(durationMs || 0));
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/speaking-audio`, { method: "POST", body: formData });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setNotice("Sprachaufnahme gespeichert.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Die Sprachaufnahme konnte nicht gespeichert werden.");
      throw err;
    } finally {
      setActionBusy(false);
    }
  }

  async function setEmployerRelease(released) {
    if (!latestAttempt?.id) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${latestAttempt.id}/employer-signal`, { method: "POST", body: JSON.stringify({ released }) });
      setNotice(released ? "Dein Sprachsignal ist jetzt fuer Firmen sichtbar." : "Dein Sprachsignal bleibt intern.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Die Freigabe konnte nicht geaendert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  const updateDraftState = (updater) => {
    setDraftState((current) => (typeof updater === "function" ? updater(current) : updater));
    setDraftDirty(true);
  };

  const completeSection = (sectionKey) => {
    if (!draftState || draftState.section_progress?.[sectionKey]?.status !== "active") return;
    if (!sectionCanBeCompleted(draftState, sectionKey)) {
      setError("Bitte schliessen Sie zuerst alle Hoer-Teile ab.");
      return;
    }
    stopListeningAudio();
    const currentIndex = WRITTEN_SECTION_ORDER.indexOf(sectionKey);
    const nextSectionKey = WRITTEN_SECTION_ORDER[currentIndex + 1] || sectionKey;
    updateDraftState((current) => withCompletedSection(current, sectionKey));
    setSelectedSectionKey(nextSectionKey);
    setSelectedSurfaceKey(nextSectionKey);
    setNotice(`${getSectionMeta(draftState.modules, sectionKey)?.title || "Abschnitt"} abgeschlossen. Der naechste Abschnitt ist jetzt frei.`);
  };

  const completeListeningPart = (partKey) => {
    const parts = draftState?.modules?.listening?.parts || [];
    const currentIndex = parts.findIndex((part) => part.key === partKey);
    const nextPartKey = parts[currentIndex + 1]?.key || "";
    updateDraftState((current) => withCompletedListeningPart(current, partKey));
    if (nextPartKey) {
      focusListeningNextStart(nextPartKey);
      setNotice("Hoer-Teil abgeschlossen. Der naechste Teil wartet auf Start.");
      return;
    }
    focusListeningChapterButton(partKey);
    setNotice("Alle Hoer-Teile sind abgeschlossen. Kapitel abschließen ist jetzt bereit.");
  };

  const updateAnswer = (moduleKey, itemId, value) => {
    if (!draftState || draftState.section_progress?.[moduleKey]?.status !== "active") return;
    let nextQuestionKey = "";
    let listeningPartToComplete = "";

    updateDraftState((current) => {
      const nextState = {
        ...current,
        [moduleKey]: {
          ...(current?.[moduleKey] || {}),
          [itemId]: Number(value),
        },
      };

      if (moduleKey === "listening") {
        const partKey = (current.modules?.listening?.items || []).find((item) => item.id === itemId)?.part_key || selectedPartKey;
        const listeningItems = (current.modules?.listening?.items || []).filter((item) => item.part_key === partKey);
        const currentIndex = listeningItems.findIndex((item) => item.id === itemId);
        const nextItem = listeningItems[currentIndex + 1];
        const partState = current.listening_runtime?.parts?.[partKey] || {};
        const allAnswered = listeningItems.length > 0 && listeningItems.every((item) => nextState.listening?.[item.id] !== undefined);

        if (nextItem) {
          nextQuestionKey = `listening:${nextItem.id}`;
        } else if (allAnswered && partState.phase === "awaiting_completion") {
          listeningPartToComplete = partKey;
        }
        return nextState;
      }

      const section = sectionCards.find((entry) => entry.key === moduleKey);
      const groups = buildSectionPartGroups(section);
      const currentPartKey = (section?.items || []).find((item) => item.id === itemId)?.part_key || selectedPartKey;
      const activeGroup = groups.find((entry) => entry.key === currentPartKey) || groups[0];
      const currentItems = activeGroup?.items || [];
      const currentIndex = currentItems.findIndex((item) => item.id === itemId);
      const nextItem = currentItems[currentIndex + 1];
      if (nextItem) {
        nextQuestionKey = `${moduleKey}:${nextItem.id}`;
      }
      return nextState;
    });

    if (listeningPartToComplete) {
      completeListeningPart(listeningPartToComplete);
      return;
    }
    if (nextQuestionKey) {
      focusQuestionKey(nextQuestionKey);
    }
  };

  const updateWritingAnswer = (value) => {
    if (!draftState || draftState.section_progress?.writing?.status !== "active") return;
    updateDraftState((current) => ({ ...current, writing_answer: value }));
  };

  const patchListeningState = (partKey, patch) => {
    updateDraftState((current) => withListeningPatch(current, partKey, patch));
  };

  const focusQuestionKey = (questionKey) => {
    if (!questionKey) return;
    setActiveQuestionKey(questionKey);
    const tryFocus = (attempt = 0) => {
      const questionNode = document.querySelector(`[data-question-key="${questionKey}"]`);
      if (!questionNode) {
        if (attempt < 6) {
          window.setTimeout(() => tryFocus(attempt + 1), 80);
        }
        return;
      }
      const focusableField = questionNode.querySelector(
        "input:not([disabled]), select:not([disabled]), textarea:not([disabled])"
      );
      if (focusableField && typeof focusableField.focus === "function") {
        try {
          focusableField.focus({ preventScroll: true });
        } catch (_err) {
          focusableField.focus();
        }
      }
      questionNode.scrollIntoView({ behavior: "smooth", block: "start" });
      const focusedQuestionKey = document.activeElement?.closest(".b1-exam-question-card")?.getAttribute("data-question-key");
      if (focusedQuestionKey !== questionKey && attempt < 6) {
        window.setTimeout(() => tryFocus(attempt + 1), 80);
      }
    };
    window.setTimeout(() => tryFocus(), 120);
  };

  const focusListeningNextStart = (partKey) => {
    if (!partKey) return;
    skipDefaultQuestionRef.current = true;
    setSelectedPartKey(partKey);
    setActiveQuestionKey("");
    window.setTimeout(() => {
      const partNode = document.querySelector(`[data-part-anchor="listening:${partKey}"]`);
      if (!partNode) return;
      partNode.scrollIntoView({ behavior: "smooth", block: "start" });
      const startButton = partNode.querySelector('[data-audio-control="start"]:not([disabled])')
        || partNode.querySelector(".b1-exam-listening-part__toggle");
      if (startButton && typeof startButton.focus === "function") {
        try {
          startButton.focus({ preventScroll: true });
        } catch (_err) {
          startButton.focus();
        }
      }
    }, 60);
  };

  const focusListeningChapterButton = (partKey) => {
    if (!partKey) return;
    skipDefaultQuestionRef.current = true;
    setSelectedPartKey(partKey);
    setActiveQuestionKey("");
    window.setTimeout(() => {
      const partNode = document.querySelector(`[data-part-anchor="listening:${partKey}"]`);
      if (!partNode) return;
      partNode.scrollIntoView({ behavior: "smooth", block: "start" });
      const completionButton = partNode.querySelector('[data-audio-control="complete-section"]:not([disabled])');
      if (completionButton && typeof completionButton.focus === "function") {
        try {
          completionButton.focus({ preventScroll: true });
        } catch (_err) {
          completionButton.focus();
        }
      }
    }, 60);
  };

  const playListeningPart = async (part) => {
    if (!activeAttempt?.id || !draftState) return;
    setListeningBusyPartKey(part.key);
    setListeningTransport({ partKey: part.key, status: "loading", currentTime: 0, duration: 0 });
    setError("");
    releaseListeningAudioElement();
    const previousPlayCount = Number(draftState.listening_runtime?.parts?.[part.key]?.play_count || 0);
    const nextPlayCount = previousPlayCount + 1;
    let playbackSettled = false;
    const resetPlayback = (message) => {
      if (playbackSettled) return;
      playbackSettled = true;
      releaseListeningAudioElement();
      setListeningTransport({ partKey: "", status: "idle", currentTime: 0, duration: 0 });
      setListeningBusyPartKey("");
      patchListeningState(part.key, {
        status: "ready_to_play",
        phase: "ready_to_play",
        play_count: previousPlayCount,
        phase_started_at: new Date().toISOString(),
        phase_deadline_at: null,
        last_error: message,
      });
      setError(message);
    };
    try {
      const blob = await fetchBlob(`/api/language-tests/b1/attempts/${activeAttempt.id}/listening-audio/${part.key}/play`, { method: "POST" });
      const objectUrl = URL.createObjectURL(blob);
      audioUrlRef.current = objectUrl;
      const audio = new Audio(objectUrl);
      audio.preload = "auto";
      audio.onloadedmetadata = () => {
        setListeningTransport((current) => current.partKey === part.key ? {
          ...current,
          duration: Number.isFinite(audio.duration) ? audio.duration : current.duration,
        } : current);
      };
      audio.ontimeupdate = () => {
        setListeningTransport((current) => current.partKey === part.key ? {
          ...current,
          currentTime: Number(audio.currentTime || 0),
          duration: Number.isFinite(audio.duration) ? audio.duration : current.duration,
        } : current);
      };
      audio.onended = () => {
        if (playbackSettled) return;
        playbackSettled = true;
        releaseListeningAudioElement();
        setListeningTransport({ partKey: "", status: "idle", currentTime: 0, duration: 0 });
        setListeningBusyPartKey("");
        patchListeningState(part.key, {
          status: "post_window",
          phase: "post_window",
          play_count: nextPlayCount,
          phase_started_at: new Date().toISOString(),
          phase_deadline_at: addSeconds(new Date().toISOString(), part.post_answer_seconds || 0),
          last_error: null,
        });
      };
      audio.onerror = () => {
        resetPlayback("Die Audio-Wiedergabe konnte im Browser nicht gestartet werden.");
      };
      audioRef.current = audio;
      await audio.play();
      if (playbackSettled) return;
      setListeningTransport((current) => current.partKey === part.key ? {
        ...current,
        status: "playing",
        currentTime: Number(audio.currentTime || 0),
        duration: Number.isFinite(audio.duration) ? audio.duration : current.duration,
      } : current);
      patchListeningState(part.key, {
        status: "playing",
        phase: "playing",
        play_count: nextPlayCount,
        phase_started_at: new Date().toISOString(),
        phase_deadline_at: null,
        last_error: null,
      });
    } catch (err) {
      if (handleAuthError(err)) return;
      resetPlayback(err.message || "Die Audio-Datei konnte nicht geladen werden.");
    }
  };

  const pauseListeningPart = (part) => {
    if (!audioRef.current || listeningBusyPartKey !== part.key) return;
    audioRef.current.pause();
    setListeningTransport((current) => current.partKey === part.key ? {
      ...current,
      status: "paused",
      currentTime: Number(audioRef.current?.currentTime || current.currentTime || 0),
      duration: Number.isFinite(audioRef.current?.duration) ? audioRef.current.duration : current.duration,
    } : current);
    setNotice(`${part.title} pausiert.`);
  };

  const resumeListeningPart = async (part) => {
    if (!audioRef.current || listeningBusyPartKey !== part.key) return;
    setError("");
    try {
      await audioRef.current.play();
      setListeningTransport((current) => current.partKey === part.key ? {
        ...current,
        status: "playing",
        currentTime: Number(audioRef.current?.currentTime || current.currentTime || 0),
        duration: Number.isFinite(audioRef.current?.duration) ? audioRef.current.duration : current.duration,
      } : current);
      setNotice(`${part.title} laeuft weiter.`);
    } catch (err) {
      setError(err.message || "Die Audio-Wiedergabe konnte nicht fortgesetzt werden.");
    }
  };

  const stopListeningPart = (part) => {
    if (listeningBusyPartKey !== part.key) return;
    const consumedPlayCount = Number(draftState?.listening_runtime?.parts?.[part.key]?.play_count || 0);
    const remainingPlays = Math.max(0, Number(part.allowed_plays || 1) - consumedPlayCount);
    stopListeningAudio();
    patchListeningState(part.key, {
      status: remainingPlays > 0 ? "ready_to_play" : "awaiting_completion",
      phase: remainingPlays > 0 ? "ready_to_play" : "awaiting_completion",
      play_count: consumedPlayCount,
      phase_started_at: new Date().toISOString(),
      phase_deadline_at: null,
      last_error: null,
    });
    setNotice(
      remainingPlays > 0
        ? `${part.title} gestoppt. Sie koennen die naechste Wiedergabe manuell starten.`
        : `${part.title} gestoppt. Die erlaubten Wiedergaben sind verbraucht.`
    );
  };

  useEffect(() => {
    if (!dialogOpen || !draftState) return;
    const activeSectionKey = draftState.active_section_key;
    if (!activeSectionKey) return;
    const activeProgress = draftState.section_progress?.[activeSectionKey];
    if (activeProgress?.status !== "active" || !activeProgress.deadline_at) return;
    if (getSecondsRemaining(activeProgress.deadline_at) > 0) return;
    completeSection(activeSectionKey);
  }, [clockTick, dialogOpen, draftState]);

  useEffect(() => {
    if (!dialogOpen || !draftState || draftState.active_section_key !== "listening") return;
    const currentPartKey = draftState.listening_runtime?.current_part_key;
    if (!currentPartKey) return;
    const part = draftState.modules?.listening?.parts?.find((entry) => entry.key === currentPartKey);
    const partState = draftState.listening_runtime?.parts?.[currentPartKey];
    if (!part || !partState?.phase_deadline_at) return;
    if (getSecondsRemaining(partState.phase_deadline_at) > 0) return;
    if (partState.phase === "reading_time") {
      patchListeningState(currentPartKey, {
        status: "ready_to_play",
        phase: "ready_to_play",
        phase_started_at: new Date().toISOString(),
        phase_deadline_at: null,
        last_error: null,
      });
    }
    if (partState.phase === "post_window") {
      const remainingPlays = Number(part.allowed_plays || 1) - Number(partState.play_count || 0);
      const items = (draftState.modules?.listening?.items || []).filter((item) => item.part_key === currentPartKey);
      const allAnswered = items.length > 0 && items.every((item) => draftState.listening?.[item.id] !== undefined);
      if (remainingPlays <= 0 && allAnswered) {
        completeListeningPart(currentPartKey);
        return;
      }
      patchListeningState(currentPartKey, {
        status: remainingPlays > 0 ? "ready_to_play" : "awaiting_completion",
        phase: remainingPlays > 0 ? "ready_to_play" : "awaiting_completion",
        phase_started_at: new Date().toISOString(),
        phase_deadline_at: null,
        last_error: null,
      });
    }
  }, [clockTick, dialogOpen, draftState]);

  useEffect(() => {
    if (!dialogOpen || !draftState || draftState.active_section_key !== "listening") return;
    const currentPartKey = draftState.listening_runtime?.current_part_key;
    if (!currentPartKey) return;
    const partState = draftState.listening_runtime?.parts?.[currentPartKey];
    if (partState?.phase !== "awaiting_completion") return;
    const items = (draftState.modules?.listening?.items || []).filter((item) => item.part_key === currentPartKey);
    const allAnswered = items.length > 0 && items.every((item) => draftState.listening?.[item.id] !== undefined);
    if (!allAnswered) return;
    completeListeningPart(currentPartKey);
  }, [dialogOpen, draftState]);

  const hasCompletedAttempt = attempts.some((attempt) => attempt.status === "completed");
  const currentModules = draftState?.modules || {};
  const sectionCards = draftState ? getSectionCards(currentModules, draftState) : [];
  const activeWrittenSectionKey = draftState?.active_section_key || findActiveSectionKey(draftState?.section_progress || {}) || "listening";
  const speakingUnlocked = draftState ? WRITTEN_SECTION_ORDER.every((key) => draftState.section_progress?.[key]?.status === "completed") : false;
  const focusSection = sectionCards.find((section) => section.key === selectedSectionKey)
    || sectionCards.find((section) => section.key === activeWrittenSectionKey)
    || sectionCards.find((section) => section.status !== "locked")
    || sectionCards[0]
    || null;
  const completedWrittenCount = draftState
    ? WRITTEN_SECTION_ORDER.filter((key) => draftState.section_progress?.[key]?.status === "completed").length
    : 0;
  const scoreCards = latestAttempt ? [
    { label: "Leseverstehen", value: latestAttempt.reading_score },
    { label: "Hörverstehen", value: latestAttempt.listening_score },
    { label: "Grammatik", value: latestAttempt.language_elements_score },
    { label: "Schreibkompetenz", value: latestAttempt.writing_score },
    { label: "Mündliche Prüfung", value: latestAttempt.speaking_score },
  ] : [];
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zurueck zur Mission Control" },
    { href: "/dashboard/applicant/sprachtest-b1", label: "Sprachtest B1", description: "Exam-Surface, Verlauf und Freigabe" },
  ]), []);
  const kvpBaseContext = useMemo(() => ({
    module_key: "language_test_b1",
    module_label: "Sprachtest B1",
    tab_key: selectedSurfaceKey || focusSection?.key || "overview",
    tab_label: focusSection?.title || "B1-Pruefung",
    last_error_code: error ? "language_test_b1_error" : null,
  }), [error, focusSection?.key, focusSection?.title, selectedSurfaceKey]);
  const coachSurfaceContext = useMemo(() => {
    const employerSignalReleased = Number(latestAttempt?.employer_signal_released || 0) === 1;
    const activeSectionLabel = focusSection?.title
      || sectionCards.find((section) => section.key === activeWrittenSectionKey)?.title
      || "B1-Pruefung";
    const hasSpeakingAudio = Boolean(draftState?.speaking_audio_meta || activeAttempt?.speaking_audio);
    const coachNextStep = activeAttempt
      ? speakingUnlocked
        ? {
            key: hasSpeakingAudio ? "b1_submit_exam" : "b1_record_speaking",
            label_de: hasSpeakingAudio ? "Pruefung absenden" : "Muendliche Pruefung aufnehmen",
            label_en: hasSpeakingAudio ? "submit exam" : "record speaking section",
            reason_de: hasSpeakingAudio
              ? "Die schriftlichen Kapitel sind abgeschlossen. Jetzt kannst du die B1-Pruefung absenden."
              : "Die schriftlichen Kapitel sind abgeschlossen. Jetzt fehlt noch die muendliche Aufnahme.",
            href: "/dashboard/applicant/sprachtest-b1",
          }
        : {
            key: `b1_continue_${toCoachKey(activeSectionLabel)}`,
            label_de: dialogOpen ? `${activeSectionLabel} fortsetzen` : "Pruefung fortsetzen",
            label_en: dialogOpen ? `continue ${getB1CoachLabelEn(activeSectionLabel)}` : "continue exam",
            reason_de: dialogOpen
              ? `Im Exam-Dialog ist ${activeSectionLabel} dein aktueller Abschnitt.`
              : "Ein geoeffneter B1-Entwurf wartet auf deinen naechsten Schritt.",
            href: "/dashboard/applicant/sprachtest-b1",
          }
      : latestAttempt && !employerSignalReleased
      ? {
          key: "b1_release_signal",
          label_de: "Deutschsignal freigeben",
          label_en: getB1CoachLabelEn("Deutschsignal freigeben"),
          reason_de: "Das letzte Ergebnis ist vorhanden, aber fuer Arbeitgeber noch nicht freigegeben.",
          href: "/dashboard/applicant/sprachtest-b1",
        }
      : {
          key: hasCompletedAttempt ? "b1_start_follow_up" : "b1_start_baseline",
          label_de: hasCompletedAttempt ? "Neuen Versuch starten" : "Ersten Versuch starten",
          label_en: getB1CoachLabelEn(hasCompletedAttempt ? "Neuen Versuch starten" : "Ersten Versuch starten"),
          reason_de: hasCompletedAttempt
            ? "Du kannst jetzt einen neuen B1-Versuch beginnen."
            : "Starte deinen ersten B1-Versuch, um ein sichtbares Deutschsignal aufzubauen.",
            href: "/dashboard/applicant/sprachtest-b1",
          };

    const coachMissingItems = [];
    if (activeAttempt && !speakingUnlocked) {
      coachMissingItems.push({
        key: `b1_${toCoachKey(activeSectionLabel)}`,
        label_de: activeSectionLabel,
        label_en: getB1CoachLabelEn(activeSectionLabel),
        reason_de: dialogOpen
          ? `Dieser Abschnitt ist im offenen Exam-Dialog dein aktueller Fokus.`
          : "Der offene Exam-Entwurf ist noch nicht abgeschlossen.",
        href: "/dashboard/applicant/sprachtest-b1",
      });
    }
    if (activeAttempt && speakingUnlocked && !hasSpeakingAudio) {
      coachMissingItems.push({
        key: "b1_speaking_audio",
        label_de: "Muendliche Pruefung",
        label_en: getB1CoachLabelEn("Muendliche Pruefung"),
        reason_de: "Die muendliche Aufnahme fehlt noch vor dem Absenden.",
        href: "/dashboard/applicant/sprachtest-b1",
      });
    }
    if (!activeAttempt && latestAttempt && !employerSignalReleased) {
      coachMissingItems.push({
        key: "b1_release_signal",
        label_de: "Freigabe fuer Arbeitgeber",
        label_en: getB1CoachLabelEn("Freigabe fuer Arbeitgeber"),
        reason_de: "Das Ergebnis ist da, aber noch intern.",
        href: "/dashboard/applicant/sprachtest-b1",
      });
    }
    if (!activeAttempt && !latestAttempt) {
      coachMissingItems.push({
        key: "b1_start_attempt",
        label_de: "B1-Versuch starten",
        label_en: getB1CoachLabelEn("B1-Versuch starten"),
        reason_de: "Ohne ersten Versuch gibt es noch kein sichtbares Deutschsignal.",
        href: "/dashboard/applicant/sprachtest-b1",
      });
    }

    return {
      screen_key: "applicant_language_test_b1",
      screen_label: "Sprachtest B1",
      highlighted_step: `${coachNextStep.label_de}: ${coachNextStep.reason_de}`,
      surface_state_key: `b1:${activeAttempt?.id || latestAttempt?.id || "none"}:${dialogOpen ? "dialog" : "page"}:${focusSection?.key || activeWrittenSectionKey || "overview"}:${draftDirty ? "dirty" : "clean"}`,
      surface_summary: {
        surface_type: "language_test_b1",
        status_label: activeAttempt
          ? (dialogOpen ? "Exam laeuft" : "Entwurf offen")
          : latestAttempt
            ? "Ergebnis verfuegbar"
            : "Noch offen",
        status_note: activeAttempt
          ? `${completedWrittenCount}/4 schriftliche Kapitel abgeschlossen.`
          : latestAttempt
            ? `Letztes Ergebnis ${latestAttempt.estimated_cefr_band || "ohne Band"} mit Score ${formatScore(latestAttempt.overall_language_score)}.`
            : "Noch kein abgeschlossener B1-Versuch vorhanden.",
        latest_cefr_band: latestAttempt?.estimated_cefr_band || "",
        latest_score: latestAttempt?.overall_language_score ?? null,
        has_active_attempt: Boolean(activeAttempt),
        has_completed_attempt: Boolean(latestAttempt),
        employer_signal_released: employerSignalReleased,
        active_section_label: activeSectionLabel,
        completed_written_count: completedWrittenCount,
        speaking_unlocked: speakingUnlocked,
        speaking_status: draftState?.speaking_status || activeAttempt?.speaking_status || "not_started",
        coach_next_step: coachNextStep,
        coach_missing_items: coachMissingItems,
      },
    };
  }, [activeAttempt, activeAttempt?.speaking_audio, activeWrittenSectionKey, completedWrittenCount, dialogOpen, draftDirty, draftState?.speaking_audio_meta, draftState?.speaking_status, focusSection, hasCompletedAttempt, latestAttempt, sectionCards, speakingUnlocked]);

  useEffect(() => {
    registerCoachSurface(coachSurfaceContext);
  }, [coachSurfaceContext, registerCoachSurface]);

  useEffect(() => {
    const nextPartKey = getFocusableParts(focusSection)?.[0]?.key || "";
    setSelectedPartKey(nextPartKey);
    setActiveQuestionKey("");
  }, [focusSection?.key]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (selectedSurfaceKey === "speaking") return;
    setSelectedSurfaceKey(focusSection?.key || "");
  }, [dialogOpen, focusSection?.key, selectedSurfaceKey]);

  useEffect(() => {
    if (!dialogOpen) return;
    const selectableKeys = sectionCards.filter((section) => section.status !== "locked").map((section) => section.key);
    const defaultSectionKey = selectableKeys.includes(activeWrittenSectionKey)
      ? activeWrittenSectionKey
      : selectableKeys[0] || activeWrittenSectionKey || null;
    if (!sectionCards.length) {
      if (selectedSectionKey !== null) {
        setSelectedSectionKey(null);
      }
      return;
    }
    const sectionStillExists = sectionCards.some((section) => section.key === selectedSectionKey);
    if (!selectedSectionKey || !sectionStillExists) {
      setSelectedSectionKey(defaultSectionKey);
    }
  }, [dialogOpen, selectedSectionKey, activeWrittenSectionKey, sectionCards]);

  const canSelectSection = (section) => Boolean(section?.key);
  const scrollStageNodeIntoView = (target) => {
    if (!target) return;
    const dialog = target.closest(".dashboard-dialog");
    if (!dialog) {
      target.scrollIntoView({ block: "start" });
      return;
    }
    const dialogRect = dialog.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = dialog.scrollTop + (targetRect.top - dialogRect.top) - 88;
    dialog.scrollTop = Math.max(0, nextTop);
  };

  const handleSelectSection = (sectionKey) => {
    const section = sectionCards.find((entry) => entry.key === sectionKey);
    if (!section || !canSelectSection(section)) return;
    setSelectedSurfaceKey(sectionKey);
    setSelectedSectionKey(sectionKey);
    window.setTimeout(() => {
      scrollStageNodeIntoView(document.querySelector(`[data-focus-section-key="${sectionKey}"]`) || focusStageRef.current);
    }, 30);
  };

  const handleSelectSpeakingStage = () => {
    setSelectedSurfaceKey("speaking");
    window.setTimeout(() => {
      scrollStageNodeIntoView(document.querySelector(".b1-exam-oral-card") || oralStageRef.current);
    }, 30);
  };

  const getFocusableParts = (section) => {
    if (!section) return [];
    if (Array.isArray(section.parts) && section.parts.length) return section.parts;
    if (section.key === "writing") {
      return [{ key: "writing_task", title: section.label || "Aufgabe", item_count: Number(section.total_items || 1) }];
    }
    return [];
  };

  const buildSectionPartGroups = (section) => {
    const items = Array.isArray(section?.items) ? section.items : [];
    const definedParts = Array.isArray(section?.parts) ? section.parts : [];
    const groups = definedParts.map((part) => ({
      ...part,
      items: items.filter((item) => item.part_key === part.key),
    }));
    const groupedKeys = new Set(definedParts.map((part) => part.key));
    const remainingItems = items.filter((item) => !item.part_key || !groupedKeys.has(item.part_key));
    if (remainingItems.length) {
      groups.push({
        key: `${section.key}_remaining`,
        title: "Aufgaben",
        item_count: remainingItems.length,
        items: remainingItems,
      });
    }
    return groups.filter((group) => group.items?.length);
  };

  const getDefaultQuestionKey = (sectionKey, partKey) => {
    if (!sectionKey) return "";
    if (sectionKey === "writing") return "writing:writing_task";
    if (sectionKey === "listening") {
      const firstListeningItem = (currentModules.listening?.items || []).find((item) => item.part_key === partKey);
      return firstListeningItem ? `listening:${firstListeningItem.id}` : "";
    }
    const section = sectionCards.find((entry) => entry.key === sectionKey);
    const groups = buildSectionPartGroups(section);
    const activeGroup = groups.find((entry) => entry.key === partKey) || groups[0];
    const firstItem = activeGroup?.items?.[0];
    return firstItem ? `${sectionKey}:${firstItem.id}` : "";
  };

  useEffect(() => {
    if (!dialogOpen || !focusSection || activeQuestionKey) return;
    if (skipDefaultQuestionRef.current) {
      skipDefaultQuestionRef.current = false;
      return;
    }
    const defaultQuestionKey = getDefaultQuestionKey(
      focusSection.key,
      selectedPartKey || getFocusableParts(focusSection)?.[0]?.key || ""
    );
    if (defaultQuestionKey) {
      setActiveQuestionKey(defaultQuestionKey);
    }
  }, [dialogOpen, focusSection, selectedPartKey, activeQuestionKey, currentModules, sectionCards]);

  const scrollToSectionPart = (sectionKey, partKey) => {
    if (!partKey) return;
    setSelectedPartKey(partKey);
    const locateAndFocusPart = (attempt = 0) => {
      const partRoot = document.querySelector(`[data-part-anchor="${sectionKey}:${partKey}"]`);
      const firstQuestionCard = partRoot?.querySelector(".b1-exam-question-card");
      const fallbackTarget = firstQuestionCard || partRoot;
      if (!fallbackTarget && attempt < 6) {
        window.setTimeout(() => locateAndFocusPart(attempt + 1), 60);
        return;
      }
      const questionKey = firstQuestionCard?.getAttribute("data-question-key") || getDefaultQuestionKey(sectionKey, partKey);
      if (questionKey) {
        setActiveQuestionKey(questionKey);
      }
      if (fallbackTarget) {
        fallbackTarget.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      const focusableField = partRoot?.querySelector(
        "input:not([disabled]), select:not([disabled]), textarea:not([disabled])"
      );
      if (focusableField && typeof focusableField.focus === "function") {
        try {
          focusableField.focus({ preventScroll: true });
        } catch (_err) {
          focusableField.focus();
        }
      }
    };
    window.setTimeout(() => locateAndFocusPart(), 40);
  };

  const renderQuestionItem = (moduleKey, item, index) => {
    const value = draftState?.[moduleKey]?.[item.id];
    const editable = draftState?.section_progress?.[moduleKey]?.status === "active";
    const isSelect = item.item_type === "matching" || item.item_type === "cloze";
    const questionKey = `${moduleKey}:${item.id}`;
    const isActive = activeQuestionKey === questionKey;
    return (
      <div
        key={item.id}
        data-question-key={questionKey}
        data-question-id={item.id}
        className={`b1-exam-question-card${isActive ? " is-active" : ""}`}
        onClick={() => setActiveQuestionKey(questionKey)}
      >
        <div className="b1-exam-question-card__head">
          <strong>{index + 1}. {item.prompt || item.statement}</strong>
          {item.part_key ? <span>{item.part_key.replace("_", " ").toUpperCase()}</span> : null}
        </div>
        {item.context ? <p className="b1-exam-question-card__context">{item.context}</p> : null}
        {isSelect ? (
          <select
            className="dashboard-textarea b1-exam-select"
            value={value === undefined ? "" : value}
            onFocus={() => setActiveQuestionKey(questionKey)}
            onChange={(event) => {
              setActiveQuestionKey(questionKey);
              updateAnswer(moduleKey, item.id, event.target.value);
            }}
            disabled={!editable}
          >
            <option value="">Bitte auswaehlen</option>
            {(item.options || []).map((option, optionIndex) => <option key={`${item.id}-${optionIndex}`} value={optionIndex}>{option}</option>)}
          </select>
        ) : (
          <div className="dashboard-question-options">
            {(item.options || []).map((option, optionIndex) => (
              <label key={`${item.id}-${optionIndex}`} className="dashboard-option-row">
                <input
                  type="radio"
                  name={`${moduleKey}-${item.id}`}
                  checked={value === optionIndex}
                  onFocus={() => setActiveQuestionKey(questionKey)}
                  onChange={() => {
                    setActiveQuestionKey(questionKey);
                    updateAnswer(moduleKey, item.id, optionIndex);
                  }}
                  disabled={!editable}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFocusSectionBody = (section) => {
    if (!section) return null;
    if (section.status === "locked") {
      return (
        <div className="b1-exam-section-preview-grid">
          {getFocusableParts(section).map((part) => (
            <article key={part.key} data-part-anchor={`${section.key}:${part.key}`} className="b1-exam-section-preview-card">
              <span className="applicant-section-kicker">{part.title}</span>
              <h4>{formatItemCountLabel(part.item_count)}</h4>
              <p>Dieser Abschnitt ist sichtbar, bleibt aber bis zur Freischaltung schreibgeschuetzt.</p>
            </article>
          ))}
        </div>
      );
    }
    if (section.key === "listening") {
      return (
        <div className="b1-exam-section-body b1-exam-section-body--listening">
          {(section.parts || []).map((part) => renderListeningPart(part))}
        </div>
      );
    }
    if (section.key === "writing") {
      const writingQuestionKey = "writing:writing_task";
      return (
        <div className="b1-exam-section-body">
          <div
            data-part-anchor={`${section.key}:writing_task`}
            data-question-key={writingQuestionKey}
            className={`b1-exam-question-card${activeQuestionKey === writingQuestionKey ? " is-active" : ""}`}
            onClick={() => setActiveQuestionKey(writingQuestionKey)}
          >
            <div className="b1-exam-question-card__head">
              <strong>{section.label || "Schreibaufgabe"}</strong>
              <span>{formatItemCountLabel(section.total_items)}</span>
            </div>
            <p className="b1-exam-question-card__context">{section.prompt}</p>
            <textarea
              className="dashboard-textarea"
              placeholder="Schreibe deine Antwort hier."
              value={draftState?.writing_answer || ""}
              onFocus={() => setActiveQuestionKey(writingQuestionKey)}
              onChange={(event) => {
                setActiveQuestionKey(writingQuestionKey);
                updateWritingAnswer(event.target.value);
              }}
              disabled={section.status !== "active"}
            />
          </div>
        </div>
      );
    }
    const itemNumberById = new Map((section.items || []).map((item, itemIndex) => [item.id, itemIndex + 1]));
    return (
      <div className="b1-exam-section-body">
        <div className="b1-exam-section-stack">
          {buildSectionPartGroups(section).map((part) => (
            <section key={part.key} data-part-anchor={`${section.key}:${part.key}`} className="b1-exam-subsection-card">
              <div className="b1-exam-subsection-card__head">
                <div>
                  <span className="applicant-section-kicker">{part.title}</span>
                  <h4>{formatItemCountLabel(part.item_count)}</h4>
                </div>
              </div>
              <div className="b1-exam-question-grid">
                {part.items.map((item) => renderQuestionItem(section.key, item, itemNumberById.get(item.id) - 1))}
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  };

  const renderListeningTransportControls = (part, partState, transportState) => {
    const transportStatus = transportState?.status || "idle";
    const lockedByOtherPart = Boolean(listeningBusyPartKey) && listeningBusyPartKey !== part.key;
    const listeningChapterReady = draftState ? allListeningPartsComplete(draftState) : false;
    const showChapterCompletion = focusSection?.key === "listening" && focusSection?.status === "active" && selectedPartKey === part.key;
    const startDisabled = actionBusy || lockedByOtherPart || transportStatus === "loading" || transportStatus === "playing" || (
      transportStatus !== "paused" && partState.phase !== "ready_to_play"
    );
    const pauseDisabled = actionBusy || listeningBusyPartKey !== part.key || transportStatus !== "playing";
    const stopDisabled = actionBusy || listeningBusyPartKey !== part.key || !["playing", "paused"].includes(transportStatus);
    return (
      <div className="b1-exam-listening-part__header-actions">
        <Button
          type="button"
          data-audio-control="start"
          className="dashboard-action-button b1-exam-listening-control"
          onClick={() => (transportStatus === "paused" ? resumeListeningPart(part) : playListeningPart(part))}
          disabled={startDisabled}
        >
          {transportStatus === "paused" ? <Play size={15} /> : <Volume2 size={15} />}
          <span>Start</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          data-audio-control="pause"
          className="dashboard-action-button dashboard-action-button-secondary b1-exam-listening-control"
          onClick={() => pauseListeningPart(part)}
          disabled={pauseDisabled}
        >
          <Pause size={15} />
          <span>Pause</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          data-audio-control="stop"
          className="dashboard-action-button dashboard-action-button-secondary b1-exam-listening-control"
          onClick={() => stopListeningPart(part)}
          disabled={stopDisabled}
        >
          <Square size={15} />
          <span>Stop</span>
        </Button>
        {showChapterCompletion ? (
          <Button
            type="button"
            data-audio-control="complete-section"
            className="dashboard-action-button b1-exam-listening-control"
            onClick={() => completeSection("listening")}
            disabled={actionBusy || !listeningChapterReady}
          >
            <ArrowRight size={15} />
            <span>Kapitel abschließen</span>
          </Button>
        ) : null}
      </div>
    );
  };

  const renderListeningPart = (part) => {
    const partState = draftState?.listening_runtime?.parts?.[part.key] || {};
    const items = (currentModules.listening?.items || []).filter((item) => item.part_key === part.key);
    const transportState = listeningTransport.partKey === part.key ? listeningTransport : null;
    const transportStatus = transportState?.status || "idle";
    const showsCountdown = partState.phase === "reading_time" || partState.phase === "post_window";
    const secondsLeft = showsCountdown ? getSecondsRemaining(partState.phase_deadline_at) : null;
    const isSelected = selectedPartKey === part.key;
    const currentQuestionKey = items.find((item) => `listening:${item.id}` === activeQuestionKey)?.id || "";
    const currentQuestionIndex = items.findIndex((item) => item.id === currentQuestionKey);
    const answeredCount = items.filter((item) => draftState?.listening?.[item.id] !== undefined).length;
    const progressPercent = transportState?.duration ? Math.min(100, Math.max(0, (transportState.currentTime / transportState.duration) * 100)) : 0;
    const phaseLabel = getListeningPhaseLabel(partState, transportStatus);
    return (
      <section key={part.key} data-part-anchor={`listening:${part.key}`} className={`b1-exam-listening-part${partState.status === "completed" ? " is-completed" : ""}${isSelected ? " is-selected" : ""}`}>
        <div className="b1-exam-listening-part__topbar">
          <button
            type="button"
            data-part-key={part.key}
            className="b1-exam-listening-part__toggle"
            onClick={() => scrollToSectionPart("listening", part.key)}
            aria-expanded={isSelected}
          >
            <div className="b1-exam-listening-part__head">
              <div>
                <span className="applicant-section-kicker">{part.title}</span>
                <h4>{formatItemCountLabel(part.item_count)}</h4>
                <p>{part.allowed_plays === 1 ? "Audio einmal" : `Audio ${part.allowed_plays} Mal`} · Einlesezeit {part.reading_time_seconds} Sekunden · Nachfenster {part.post_answer_seconds} Sekunden</p>
              </div>
              <div className="b1-exam-listening-part__status">
                <span>{partState.play_count || 0}/{part.allowed_plays} Wiedergaben</span>
                <strong>{phaseLabel}</strong>
                {secondsLeft !== null && partState.status !== "completed" ? <small>{formatCountdown(secondsLeft)}</small> : <small>{transportStatus === "paused" ? "lokal pausiert" : " "}</small>}
              </div>
            </div>
            <span className={`b1-exam-listening-part__chevron${isSelected ? " is-open" : ""}`} aria-hidden="true"><ChevronDown size={18} /></span>
          </button>
          {renderListeningTransportControls(part, partState, transportState)}
        </div>
        {partState.last_error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{partState.last_error}</div> : null}
        {isSelected ? (
          <div className="b1-exam-listening-part__body">
            <aside className="b1-exam-listening-console">
              <div className="b1-exam-listening-console__chips">
                <span className="b1-exam-listening-console__chip is-active">
                  {currentQuestionIndex >= 0 ? `Aktiv: Aufgabe ${currentQuestionIndex + 1}` : "Aktiv: Aufgabe 1"}
                </span>
                <span className="b1-exam-listening-console__chip">{answeredCount}/{items.length} beantwortet</span>
                <span className="b1-exam-listening-console__chip">{phaseLabel}</span>
              </div>
              <div className="b1-exam-listening-console__meter">
                <div>
                  <span className="dashboard-question-card__meta">Audio-Konsole</span>
                  <strong>{transportStatus === "playing" ? "Wiedergabe aktiv" : transportStatus === "paused" ? "Wiedergabe pausiert" : transportStatus === "loading" ? "Audio startet" : "Naechste Aktion vorbereiten"}</strong>
                </div>
                <div className="b1-exam-listening-console__time">
                  <span>{formatAudioTime(transportState?.currentTime || 0)}</span>
                  <small>von {formatAudioTime(transportState?.duration)}</small>
                </div>
                <div className="b1-exam-listening-console__progress" aria-hidden="true">
                  <span style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
              <p className="b1-exam-listening-console__note">
                Start startet die offizielle Wiedergabe. Pause haelt nur lokal an. Stop beendet die laufende Wiedergabe dieses Teils.
              </p>
              {focusSection?.key === "listening" && focusSection?.status === "active" ? (
                <p className="b1-exam-listening-console__note">
                  {allListeningPartsComplete(draftState)
                    ? "Alle Hoerteile sind abgeschlossen. Du kannst das Kapitel jetzt direkt oben neben den Audio-Controls abschließen."
                    : "Kapitel abschließen wird erst freigeschaltet, sobald alle drei Hoerteile abgeschlossen sind."}
                </p>
              ) : null}
              {partState.phase === "awaiting_completion" ? (
                <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => completeListeningPart(part.key)}>
                  <CheckCircle2 size={16} />
                  <span>Teil abschliessen</span>
                </Button>
              ) : null}
            </aside>
            <div className="b1-exam-listening-items">{items.map((item, index) => renderQuestionItem("listening", item, index))}</div>
          </div>
        ) : (
          <div className="b1-exam-listening-part__collapsed-note">Klicken Sie auf diesen Teil, um die Aufgaben und Audio-Aktionen zu öffnen.</div>
        )}
      </section>
    );
  };

  return (
    <ApplicantShell
      title="Sprachtest B1"
      subtitle="Experts4Europe-B1-Prüfung mit vier schriftlichen Abschnitten, echtem Hör-Audio und separater mündlicher Prüfung."
      actions={<><Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant")}>Zur Mission Control</Button><Button className="applicant-cta-button" onClick={() => activeAttempt ? setDialogOpen(true) : startAttempt(hasCompletedAttempt ? "follow_up" : "baseline")} disabled={loading || actionBusy}>{activeAttempt ? "Pruefung fortsetzen" : hasCompletedAttempt ? "Neuen Versuch starten" : "Pruefung starten"}</Button></>}
      notifications={[]}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        {notice ? <div className="dashboard-inline-alert dashboard-inline-alert-success">{notice}</div> : null}
        <section className="applicant-workspace-hero applicant-workspace-hero--b1">
          <div className="applicant-workspace-hero__main">
            <div className="applicant-workspace-card__head applicant-workspace-card__head--inline">
              <div className="applicant-workspace-heading-block">
                <span className="applicant-section-kicker">Experts4Europe-B1-Prüfung</span>
                <div className="applicant-workspace-title-row">
                  <h2>Vier schriftliche Abschnitte. Danach die mündliche Prüfung.</h2>
                  <KvpReportTrigger
                    context={{
                      ...kvpBaseContext,
                      section_key: "exam_overview",
                      section_label: "B1-Pruefung",
                    }}
                  />
                </div>
                <p>Hörverstehen, Leseverstehen, Grammatik und Schreibkompetenz laufen jetzt als klar getrennte Exam-Abschnitte mit sichtbaren Zeiten, Locking und echtem Hör-Audio.</p>
              </div>
            </div>
          </div>
          <aside className="applicant-readiness-panel applicant-readiness-panel--language">
            <div className="applicant-readiness-orb applicant-readiness-orb--language">
              <strong>{latestAttempt?.overall_language_score ? Math.round(Number(latestAttempt.overall_language_score)) : "--"}</strong>
              <span>Deutsch-Score</span>
              <small>{latestAttempt?.estimated_cefr_band || "wird geladen"}</small>
            </div>
          </aside>
        </section>
        <section className="applicant-workspace-scoreboard">
          {scoreCards.length ? scoreCards.map((item) => (<article key={item.label} className="applicant-signal-tile"><span>{item.label}</span><strong>{formatScore(item.value)}</strong><small>Modulwert</small></article>)) : <div className="applicant-empty-card">Nach dem ersten abgeschlossenen Versuch siehst du hier deine Abschnittswerte.</div>}
        </section>
        <div className="applicant-workspace-grid applicant-workspace-grid--main">
          <section className="applicant-workspace-card"><div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Jetzt</span><h3>Dein Exam-Status</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "exam_status", section_label: "Dein Exam-Status" }} /></div><div className="applicant-checklist"><div className="applicant-checklist__item is-done"><BadgeCheck size={16} /><span>{latestAttempt ? `Letztes Ergebnis: ${latestAttempt.estimated_cefr_band || "Noch offen"}` : "Noch kein abgeschlossener Versuch"}</span></div><div className="applicant-checklist__item is-open"><Languages size={16} /><span>{activeAttempt ? "Ein Exam-Entwurf wartet auf dich." : hasCompletedAttempt ? "Du kannst jetzt einen neuen Versuch starten." : "Starte deinen ersten B1-Versuch."}</span></div><div className="applicant-checklist__item is-open"><Headphones size={16} /><span>Hörverstehen läuft mit echter Audio-Wiedergabe und Headset-Flow.</span></div></div></section>
          <section className="applicant-workspace-card"><div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Freigabe</span><h3>Fuer Arbeitgeber sichtbar</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "employer_release", section_label: "Fuer Arbeitgeber sichtbar" }} /></div><p className="applicant-workspace-card__copy">Nur CEFR-Band, Gesamtwert und Trend werden geteilt. Audio, Schreiben und Exam-Details bleiben intern.</p><div className="applicant-workspace-actions"><Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => setEmployerRelease(false)} disabled={!latestAttempt || actionBusy}>Intern halten</Button><Button className="applicant-cta-button" onClick={() => setEmployerRelease(true)} disabled={!latestAttempt || actionBusy}>Freigeben</Button></div></section>
        </div>
      </div>
      <Dialog open={dialogOpen} onOpenChange={(nextOpen) => { setDialogOpen(nextOpen); if (!nextOpen) stopListeningAudio(); }}>
        <DialogContent
          fullScreen
          className="dashboard-dialog b1-exam-dialog"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader className="dashboard-dialog__header"><DialogTitle className="dashboard-dialog__title">Experts4Europe-B1-Prüfung</DialogTitle><DialogDescription className="dashboard-dialog__description">Vier schriftliche Abschnitte mit sichtbaren Zeiten, echtem Hör-Audio und nachgelagerter mündlicher Prüfung.</DialogDescription></DialogHeader>
          <div className="dashboard-dialog__content b1-exam-dialog__content">
            <div className="dashboard-dialog__main b1-exam-dialog__main">
              <section className="b1-exam-nav-panel">
                <div className="b1-exam-nav-panel__intro">
                  <div className="applicant-workspace-card__head applicant-workspace-card__head--inline">
                    <div className="applicant-workspace-heading-block">
                      <span className="applicant-section-kicker">Kapitel-Navigation</span>
                      <div className="applicant-workspace-title-row">
                        <h3>Kapitelübersicht</h3>
                        <KvpReportTrigger
                          context={{
                            ...kvpBaseContext,
                            section_key: "chapter_overview",
                            section_label: "Kapiteluebersicht",
                          }}
                        />
                      </div>
                      <p>Wähle ein freigeschaltetes Kapitel. Immer nur ein Abschnitt ist als große Arbeitsfläche geöffnet.</p>
                    </div>
                  </div>
                  <div className="b1-exam-nav-panel__progress">
                    <strong>{completedWrittenCount}/4 abgeschlossen</strong>
                    <small>Aktiv: {sectionCards.find((section) => section.key === activeWrittenSectionKey)?.title || "Noch offen"}</small>
                  </div>
                </div>
                <section className="b1-exam-stage-grid">
                  {sectionCards.map((section) => {
                    const StatusIcon = getStatusIcon(section.status);
                    const SectionIcon = SECTION_ICONS[section.key] || CircleDashed;
                    const secondsLeft = getSecondsRemaining(section.deadline_at);
                    const isSelected = selectedSurfaceKey === section.key;
                    const isLocked = section.status === "locked";
                    return (
                      <button
                        key={section.key}
                        type="button"
                        data-section-key={section.key}
                        data-surface-key={section.key}
                        className={`b1-exam-stepper__card b1-exam-stage-tile is-${section.status}${isSelected ? " is-selected" : ""}`}
                        onClick={() => handleSelectSection(section.key)}
                        aria-pressed={isSelected}
                      >
                        <div className="b1-exam-stepper__eyebrow">
                          <span className="b1-exam-stepper__status-pill"><StatusIcon size={14} /> {renderStatusLabel(section.status)}</span>
                          {secondsLeft !== null && section.status === "active" ? <small>{formatCountdown(secondsLeft)}</small> : <small>{isLocked ? "Vorschau" : "Oeffnen"}</small>}
                        </div>
                        <div className="b1-exam-stepper__head">
                          <span className="b1-exam-stepper__icon"><SectionIcon size={18} /></span>
                          <div>
                            <strong>{section.title}</strong>
                            <p>{section.instructions || "Kapitel oeffnen und in der Arbeitsflaeche bearbeiten."}</p>
                          </div>
                        </div>
                        <div className="b1-exam-stepper__metrics">
                          <span>{formatDurationLabel(section.duration_minutes)}</span>
                          <span>{formatItemCountLabel(section.total_items)}</span>
                          <span>{(section.parts || []).map((part) => part.item_count).join(" / ") || section.label || "Ein Teil"}</span>
                        </div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    data-surface-key="speaking"
                    className={`b1-exam-stepper__card b1-exam-stage-tile is-${speakingUnlocked ? "available" : "locked"}${selectedSurfaceKey === "speaking" ? " is-selected" : ""}`}
                    onClick={handleSelectSpeakingStage}
                    aria-pressed={selectedSurfaceKey === "speaking"}
                  >
                    <div className="b1-exam-stepper__eyebrow">
                      <span className="b1-exam-stepper__status-pill"><Mic2 size={14} /> {speakingUnlocked ? "separat" : "nachgelagert"}</span>
                      <small>{speakingUnlocked ? "Zum Recorder" : "Vorschau"}</small>
                    </div>
                    <div className="b1-exam-stepper__head">
                      <span className="b1-exam-stepper__icon"><Mic2 size={18} /></span>
                      <div>
                        <strong>{currentModules.speaking?.title || "Muendliche Pruefung"}</strong>
                        <p>Bleibt technisch getrennt und oeffnet den nachgelagerten Audio-Bereich.</p>
                      </div>
                    </div>
                    <div className="b1-exam-stepper__metrics">
                      <span>3 Teile</span>
                      <span>20 Minuten Vorbereitung</span>
                      <span>{currentModules.speaking?.exam_duration_minutes || "ca. 20-30 Minuten"}</span>
                    </div>
                  </button>
                </section>
              </section>

              {focusSection ? (
                <section ref={focusStageRef} key={focusSection.key} data-focus-section-key={focusSection.key} className={`b1-exam-focus-stage is-${focusSection.status}`}>
                  <header className="b1-exam-focus-stage__header">
                    <div className="applicant-workspace-card__head applicant-workspace-card__head--inline">
                      <div className="applicant-workspace-heading-block">
                        <span className="applicant-section-kicker">Aktive Arbeitsfläche</span>
                        <div className="applicant-workspace-title-row">
                          <h3>{focusSection.title}</h3>
                          <KvpReportTrigger
                            context={{
                              ...kvpBaseContext,
                              section_key: focusSection.key,
                              section_label: focusSection.title,
                            }}
                          />
                        </div>
                        <p>{focusSection.instructions}</p>
                      </div>
                    </div>
                    <div className="b1-exam-focus-stage__meta">
                      <div className="b1-exam-meta-pill"><Clock3 size={15} /><span>{formatDurationLabel(focusSection.duration_minutes)}</span></div>
                      <div className="b1-exam-meta-pill"><BadgeCheck size={15} /><span>{formatItemCountLabel(focusSection.total_items)}</span></div>
                      <div className={`b1-exam-meta-pill is-${focusSection.status}`}><span>{renderStatusLabel(focusSection.status)}</span></div>
                    </div>
                  </header>
                  <div className="b1-exam-focus-stage__parts">
                    {getFocusableParts(focusSection).map((part) => (
                      <button
                        key={part.key}
                        type="button"
                        data-part-key={part.key}
                        className={`b1-exam-part-chip${selectedPartKey === part.key ? " is-selected" : ""}`}
                        onClick={() => scrollToSectionPart(focusSection.key, part.key)}
                      >
                        {part.title}: {part.item_count}
                      </button>
                    ))}
                  </div>
                  {focusSection.status === "completed" ? <div className="b1-exam-complete-state"><CheckCircle2 size={16} /><span>Dieses Kapitel ist abgeschlossen und bleibt nur noch lesbar.</span></div> : null}
                  {focusSection.status === "locked" ? <div className="b1-exam-locked-state"><Lock size={16} /><span>Dieses Kapitel ist noch gesperrt und kann erst nach dem Vorabschnitt bearbeitet werden.</span></div> : null}
                  {renderFocusSectionBody(focusSection)}
                  {focusSection.status === "active" ? (
                    <footer className="b1-exam-focus-stage__footer">
                      <div className="b1-exam-inline-note">
                        {focusSection.key === "listening"
                          ? "Antworten bleiben während Audio und Nachfenster möglich. Nach dem Abschluss wird sofort das nächste Kapitel freigeschaltet."
                          : "Du kannst diesen Abschnitt vorzeitig abschließen und direkt in das nächste Kapitel wechseln."}
                      </div>
                      {focusSection.key !== "listening" ? (
                        <Button className="dashboard-action-button" onClick={() => completeSection(focusSection.key)}>
                          <ArrowRight size={16} />
                          <span>Kapitel abschließen</span>
                        </Button>
                      ) : null}
                    </footer>
                  ) : null}
                </section>
              ) : null}

              <section ref={oralStageRef} className={`b1-exam-oral-card${speakingUnlocked ? " is-open" : " is-locked"}`}>
                <header className="b1-exam-oral-card__header">
                  <div className="applicant-workspace-card__head applicant-workspace-card__head--inline">
                    <div className="applicant-workspace-heading-block">
                      <span className="applicant-section-kicker">Danach separat</span>
                      <div className="applicant-workspace-title-row">
                        <h3>{currentModules.speaking?.title || "Muendliche Pruefung"}</h3>
                        <KvpReportTrigger
                          context={{
                            ...kvpBaseContext,
                            section_key: "speaking",
                            section_label: currentModules.speaking?.title || "Muendliche Pruefung",
                          }}
                        />
                      </div>
                      <p>3 Teile · 20 Minuten Vorbereitung · {currentModules.speaking?.exam_duration_minutes || "ca. 20-30 Minuten"}</p>
                    </div>
                  </div>
                  <div className="b1-exam-meta-pill"><Mic2 size={15} /><span>Bestehender Audio-Pfad</span></div>
                </header>
                {!speakingUnlocked ? <div className="b1-exam-locked-state"><Lock size={16} /><span>Die mündliche Prüfung bleibt nachgelagert und wird erst nach den vier schriftlichen Kapiteln freigeschaltet.</span></div> : <div className="b1-exam-oral-card__body"><div className="b1-exam-question-card"><p className="dashboard-question-card__meta">Audioaufgabe</p><ul className="dashboard-bullet-list">{(currentModules.speaking?.prompts || []).map((prompt) => <li key={prompt}>{prompt}</li>)}</ul></div><B1SpeakingAudioRecorder attemptId={activeAttempt?.id} currentAudio={activeAttempt?.speaking_audio || draftState?.speaking_audio_meta} speakingStatus={activeAttempt?.speaking_status || draftState?.speaking_status} invalidReason={activeAttempt?.speaking_invalid_reason || draftState?.speaking_invalid_reason} prompts={currentModules.speaking?.prompts || []} disabled={actionBusy || !activeAttempt} onUpload={uploadSpeakingAudio} /></div>}
              </section>
            </div>
            <aside className="dashboard-dialog__side b1-exam-dialog__side">
              <section className="dashboard-dialog__section b1-exam-side-card">
                <h3>Arbeitsraum</h3>
                <div className="b1-exam-side-summary">
                  <div className="b1-exam-side-summary__row"><span>Versuch</span><strong>{formatAttemptType(activeAttempt?.test_type)}</strong></div>
                  <div className="b1-exam-side-summary__row"><span>Version</span><strong>{activeAttempt?.core_version || "b1_exam_v2"}</strong></div>
                  <div className="b1-exam-side-summary__row"><span>Aktives Kapitel</span><strong>{sectionCards.find((section) => section.key === activeWrittenSectionKey)?.title || "Noch offen"}</strong></div>
                  <div className="b1-exam-side-summary__row"><span>Stand</span><strong>{draftDirty ? "Ungespeichert" : "Synchron"}</strong></div>
                </div>
                <div className="dashboard-dialog__actions b1-exam-side-card__actions">
                  <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => saveDraft()} disabled={actionBusy || !activeAttempt}>Entwurf speichern</Button>
                  <Button className="dashboard-action-button" onClick={submitAttempt} disabled={actionBusy || !activeAttempt || !speakingUnlocked || !(activeAttempt?.speaking_audio || draftState?.speaking_audio_meta)}>Pruefung absenden</Button>
                </div>
              </section>
              <section className="dashboard-dialog__section b1-exam-side-card">
                <h3>Wiedereinstieg</h3>
                <div className="b1-exam-side-summary">
                  {sectionCards.map((section) => (
                    <button key={section.key} type="button" data-section-key={section.key} className={`b1-exam-side-section is-${section.status}${focusSection?.key === section.key ? " is-current" : ""}`} onClick={() => handleSelectSection(section.key)}>
                      <span>{section.title}</span>
                      <strong>{renderStatusLabel(section.status)}</strong>
                    </button>
                  ))}
                </div>
              </section>
              <section className="dashboard-dialog__section b1-exam-side-card">
                <div className="applicant-workspace-card__head applicant-workspace-card__head--inline">
                  <h3>Historie und Audio</h3>
                  <KvpReportTrigger
                    context={{
                      ...kvpBaseContext,
                      section_key: "history_audio",
                      section_label: "Historie und Audio",
                    }}
                  />
                </div>
                <Accordion type="single" collapsible className="dashboard-accordion">
                  <AccordionItem value="attempts" className="dashboard-accordion__item">
                    <AccordionTrigger className="dashboard-accordion__trigger">Letzte Versuche</AccordionTrigger>
                    <AccordionContent className="dashboard-accordion__content">
                      <div className="dashboard-history-list">{attempts.length ? attempts.slice(0, 4).map((attempt) => <div key={attempt.id} className="dashboard-history-item"><div><strong>Versuch #{attempt.id}</strong><p>{formatAttemptType(attempt.test_type)} · {formatReviewStatus(attempt.review_status)}</p></div><div className="dashboard-history-meta"><span>{attempt.estimated_cefr_band || "Noch offen"}</span><small>{formatDate(attempt.completed_at || attempt.created_at)}</small></div></div>) : <div className="dashboard-empty-tile">Noch keine gespeicherten Versuche vorhanden.</div>}</div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <div className="b1-exam-audio-note"><Headphones size={16} /><span>Hörverstehen startet erst nach Browser-Interaktion und bleibt pro Teil auf die erlaubte Anzahl an Wiedergaben begrenzt.</span></div>
                <div className="b1-exam-audio-note"><Mic2 size={16} /><span>Die mündliche Prüfung nutzt weiter den bestehenden Recorder und bleibt technisch vom Hör-Audio getrennt.</span></div>
                <div className="b1-exam-audio-note"><AlertTriangle size={16} /><span>Wenn Audio nicht geladen werden kann, bleibt der Abschnitt offen und zeigt einen klaren Fehler statt eines stillen Abschlusses.</span></div>
              </section>
            </aside>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}><DialogContent className="max-w-[460px]"><DialogHeader><DialogTitle>Sitzung abgelaufen</DialogTitle><DialogDescription>{sessionExpiredMessage}</DialogDescription></DialogHeader><div className="flex flex-wrap justify-end gap-3"><Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button><Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button></div></DialogContent></Dialog>
      <ApplicantCoachPanel />
    </ApplicantShell>
  );
}
